import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Star, Loader2, Image as ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export function ReviewFormDialog({
  open,
  onOpenChange,
  reviewForm,
  setReviewForm,
  onSubmit,
  submitting,
  onPhotoUpload,
  photoUploading,
}) {
  const { t } = useI18n();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }}>{t('review.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Overall Rating */}
          <div>
            <label className="text-sm font-medium text-[#1C1917] mb-2 block">{t('review.overall')}</label>
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setReviewForm({ ...reviewForm, overall_rating: s })}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-overall-${s}`}
                >
                  <Star className={`w-8 h-8 ${s <= reviewForm.overall_rating ? 'text-[#C36F4D] fill-[#C36F4D]' : 'text-[#E7E5E4]'}`} />
                </button>
              ))}
              <span className="ml-2 font-semibold text-[#1C1917]">{reviewForm.overall_rating}/5</span>
            </div>
          </div>

          {/* Category Ratings */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-[#1C1917]">{t('review.categories')}</label>
            {[
              { key: 'cleanliness', label: t('review.cleanliness') },
              { key: 'location', label: t('review.location') },
              { key: 'service', label: t('review.service') },
              { key: 'value', label: t('review.value') },
              { key: 'amenities', label: t('review.amenities') }
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between gap-2">
                <span className="text-sm text-[#57534E] w-24 sm:w-40 flex-shrink-0">{label}</span>
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setReviewForm({
                        ...reviewForm,
                        categories: { ...reviewForm.categories, [key]: s }
                      })}
                      className="p-0.5"
                      data-testid={`star-${key}-${s}`}
                    >
                      <Star className={`w-5 h-5 ${s <= reviewForm.categories[key] ? 'text-[#C36F4D] fill-[#C36F4D]' : 'text-[#E7E5E4]'}`} />
                    </button>
                  ))}
                  <span className="ml-1 text-xs text-[#78716C] w-6">{reviewForm.categories[key]}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Comment */}
          <div>
            <label className="text-sm font-medium text-[#1C1917] mb-2 block">{t('review.comment')}</label>
            <Textarea
              value={reviewForm.comment}
              onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
              placeholder={t('review.commentPlaceholder')}
              rows={4}
              className="border-[#E7E5E4]"
              data-testid="review-comment"
            />
            <p className="text-xs text-[#78716C] mt-1">{reviewForm.comment.length}/2000</p>
          </div>

          {/* Photo Upload */}
          <div data-testid="review-photo-section">
            <label className="text-sm font-medium text-[#1C1917] mb-2 block">{t('review.photos')}</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {reviewForm.photos.map((photo, idx) => (
                <div key={`photo-${photo}`} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E7E5E4]">
                  <img src={photo.startsWith('/api') ? `${API}${photo}` : photo} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setReviewForm(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }))}
                    className="absolute top-0 right-0 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-bl"
                  >x</button>
                </div>
              ))}
              {reviewForm.photos.length < 5 && (
                <label className="w-20 h-20 rounded-lg border-2 border-dashed border-[#E7E5E4] flex flex-col items-center justify-center cursor-pointer hover:border-[#264639] transition-colors">
                  {photoUploading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-[#78716C]" />
                  ) : (
                    <>
                      <ImageIcon className="w-5 h-5 text-[#78716C]" />
                      <span className="text-[10px] text-[#78716C] mt-1">{t('review.addPhoto')}</span>
                    </>
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={onPhotoUpload} disabled={photoUploading} />
                </label>
              )}
            </div>
            <p className="text-xs text-[#78716C]">{reviewForm.photos.length}/5 fotos</p>
          </div>

          <Button
            onClick={onSubmit}
            disabled={submitting || reviewForm.comment.length < 10}
            className="w-full bg-[#264639] hover:bg-[#1C352B] py-5"
            data-testid="submit-review-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publicando...</>
            ) : (
              <><Star className="w-4 h-4 mr-2" /> Publicar Reseña</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
